import {ILoesungshaeufigkeitenWerte, IPortalseite, ISchueler_testsAPI, ITestheftAPI} from "../Interfaces";
import Utilities from "./../Utilities";
import {IErgebnisseDB, ILoesungshaeufigkeitenDB} from "../InterfacesDB";
import Rueckmeldung from "./Rueckmeldung";
import Tooltip from '../Tooltip';
import TooltipItem from '../TooltipItem';

type ILhItemebeneVergleichsgruppen = ILoesungshaeufigkeitenDB & {
    subgruppe_kuerzel?: string;
    kontextgruppe_bezeichnung?: string;
};

export default class RueckmeldungAufgabenebene extends Rueckmeldung implements IPortalseite {
    private isSchulwertEnabled: boolean = true;
    private isLandeswertEnabled: boolean = false;
    private isVergleichsgruppeEnabled: boolean = false;
    private niveaustufeFilter: string = '-';

    override async onSeiteLoad(): Promise<boolean> {
        await super.onSeiteLoad();
        await this.filterAnwenden();
        this.refreshDruckkopf();
        this.positioniereDetailansichtOption();
        this.renderNiveaustufeFilter();
        this.refreshNiveaustufeFilter([]);

        //Spaltenköpfe einbinden und Sortierung initialisieren
        if (this.domaenenInGruppe.length === 0)
            return true;

        document.querySelector(`div.rueckmeldung-lh-aufgabenebene > div:nth-of-type(1)`).innerHTML = `
        <div class="header">
            <div class="eigenschaften" data-sort-kategorie="eigenschaften">Item / Aufgabe</div>
            <div class="loesungshaeufigkeit" data-sort-kategorie="loesungshaeufigkeit">Lösungshäufigkeit</div>
            <div class="domaene" data-sort-kategorie="domaene">${(this.sessionvariablen.get['fach'] !== 'ma') ? `Domäne` : `Leitidee`}</div>
            ${(this.sessionvariablen.get['fach'] === 'ma') ? `<div class="kompetenzbereiche">mathematische<br/>Kompetenzen</div>` : ``}
        </div>
        `;

        await this.initSortierungSpaltenkoepfe();

        // initiale Sortierung anzeigen
        document.addEventListener('refresh-rueckmeldung', () => {
            this.refreshLhAufgabenebene();
        });
        document.dispatchEvent(new Event('refresh-rueckmeldung'));

        return true;
    }

    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    private refreshLhAufgabenebene(): void {
        const isVergleichswerteFreigegeben =
            !!this.sessionvariablen?.land?.kompetenzstufenrueckmeldung_start_am &&
            this.portalzeit >= new Date(this.sessionvariablen.land.kompetenzstufenrueckmeldung_start_am);

        this.isLandeswertEnabled =
            isVergleichswerteFreigegeben &&
            !['nw', 'ni'].includes(this.sessionvariablen.schule.land);
        this.isVergleichsgruppeEnabled =
            isVergleichswerteFreigegeben &&
            ['nw', 'hb'].includes(this.sessionvariablen.schule.land);
        this.refreshDruckkopf();

        // Tabelle nicht anzeigen, wenn alle SuS F sind
        let testsInGruppe = Utilities.cloneObject(
            this.testsInGruppe.filter(t =>
                this.schuelerliste
                    .filter(s => (s.schueler_id === t.schueler_id))[0].is_f === 0)
        ) as ISchueler_testsAPI[];

        //region Hinweistext einblenden, wenn Fach und Gruppe benötigt werden / niemand das Fach testet
        if (testsInGruppe.length === 0) {
            if (this.sessionvariablen.get['fach'] && this.sessionvariablen.get['gruppeid']) {
                document.querySelector(`div.rueckmeldung-lh-aufgabenebene`).innerHTML =
                    `Keine Schüler
                        ${((this.gruppeAngezeigt.typ === 'STAMMKLASSE') ? 'der Stammklasse' : 'des Kurses') ?? 'der Gruppe'} <i>${this.gruppeAngezeigt.bezeichnung ?? ''}</i>
                        haben Ergebnisse im Fach ${Utilities.printFachLang(this.sessionvariablen.get['fach'])} eingetragen.
                        `;
            } else {
                document.querySelector(`div.rueckmeldung-lh-aufgabenebene`).innerHTML = `Wählen Sie ein Fach und eine Gruppe aus, für die Sie Rückmeldungen einsehen möchten.`;
            }
            return;
        }
        //endregion

        //region Gruppenergebnisse berechnen
        let ergebnisseInGruppe = this.testsInGruppe
            .filter(t => t.nicht_anwesend === 0)
            .filter(t => {
                let schueler = this.schuelerliste.filter(s => s.schueler_id === t.schueler_id)[0] ?? null;
                if (schueler === null)
                    return false;

                const land = this.sessionvariablen?.schule?.land;

                // F immer ausschließen
                if (schueler.is_f === 1) return false;

                // S bei NI miteinberechnen
                if (land !== 'ni' && schueler.is_s === 1) return false;

                return true;
            })
            .flatMap(t => t.ergebnisse)

        let itemErgebisse: {
            testheft_idArr: number[];
            item_id: number;
            ergebnisse: (IErgebnisseDB & { testheft: ITestheftAPI })[];
            loesungshaeufigkeiten: ILoesungshaeufigkeitenWerte;
        }[] = [];

        ergebnisseInGruppe.forEach(e => {
            if (itemErgebisse.map(i => i.item_id).includes(e.item_id) === false) {
                itemErgebisse.push({
                    testheft_idArr: [e.testheft_id],
                    item_id: e.item_id,
                    ergebnisse: [],
                    loesungshaeufigkeiten: {
                        anzahl: null,
                        r: null,
                        n: null,
                        f: null,
                        rProz: null,
                        nProz: null,
                        fProz: null
                    }
                });
            } else {
                if (
                    itemErgebisse
                        .filter(i => i.item_id === e.item_id)[0].testheft_idArr
                        .includes(e.testheft_id) === false
                )
                    itemErgebisse.filter(i => i.item_id === e.item_id)[0].testheft_idArr.push(e.testheft_id);
            }

            //region Testheft-Property nur auf das Testheft, das Modul, die Aufgabe, reduzieren, in denen dieses Item getestet wurde
            let ergebnisMitTestheft: IErgebnisseDB & { testheft: ITestheftAPI } = Utilities.cloneObject(e) as IErgebnisseDB & { testheft: ITestheftAPI };
            ergebnisMitTestheft.testheft = Utilities.cloneObject(this.testheftliste.filter(th => th.testheft_id === e.testheft_id)[0]);
            ergebnisMitTestheft.testheft.module = ergebnisMitTestheft.testheft.module
                .filter(m => m.aufgaben
                    .flatMap(a => a.items)
                    .flatMap(i => i.item_id).includes(e.item_id)
                );
            ergebnisMitTestheft.testheft.module[0].aufgaben = ergebnisMitTestheft.testheft.module[0].aufgaben
                .filter(a => a.items
                    .flatMap(i => i.item_id).includes(e.item_id)
                );
            ergebnisMitTestheft.testheft.module[0].aufgaben[0].items = ergebnisMitTestheft.testheft.module[0].aufgaben[0].items
                .filter(i => i.item_id === e.item_id
                );
            itemErgebisse.filter(i => i.item_id === e.item_id)[0].ergebnisse.push(ergebnisMitTestheft);
            //endregion

        });

        // Lösungshäufigkeiten für dieses Item berechnen
        itemErgebisse.forEach(i => {
            i.loesungshaeufigkeiten.anzahl = i.ergebnisse.length;
            i.loesungshaeufigkeiten.r = i.ergebnisse.filter(e => e.ergebnis === 1).length;
            i.loesungshaeufigkeiten.n = i.ergebnisse.filter(e => e.ergebnis === -1).length;
            i.loesungshaeufigkeiten.f = i.ergebnisse.filter(e => e.ergebnis === 0).length;

            let proz: number[] = [
                (i.loesungshaeufigkeiten.r / i.loesungshaeufigkeiten.anzahl) * 100,
                (i.loesungshaeufigkeiten.n / i.loesungshaeufigkeiten.anzahl) * 100,
                (i.loesungshaeufigkeiten.f / i.loesungshaeufigkeiten.anzahl) * 100
            ];
            let prozGerundet: number[] = Utilities.summenerhaltendRunden(proz);

            i.loesungshaeufigkeiten.rProz = prozGerundet[0];
            i.loesungshaeufigkeiten.nProz = prozGerundet[1];
            i.loesungshaeufigkeiten.fProz = prozGerundet[2];
        });
        //endregion

        //region Schulwert berechnen
        let ergebnisseInSchule = this.testsInSchule
            .filter(t => t.nicht_anwesend === 0)
            .filter(t => {
                let schueler = this.schuelerliste.filter(s => s.schueler_id === t.schueler_id)[0] ?? null;
                if (schueler === null)
                    return false;

                const land = this.sessionvariablen?.schule?.land;

                // F immer ausschließen
                if (schueler.is_f === 1) return false;

                // S bei NI miteinberechnen
                if (land !== 'ni' && schueler.is_s === 1) return false;

                return true;
            })
            .flatMap(t => t.ergebnisse)

        let itemErgebisseInSchule: {
            testheft_idArr: number[];
            item_id: number;
            ergebnisse: (IErgebnisseDB & { testheft: ITestheftAPI })[];
            loesungshaeufigkeiten: ILoesungshaeufigkeitenWerte;
        }[] = [];

        ergebnisseInSchule.forEach(e => {
            //TODO:medium gleiche Aufgaben werden derzeit über alle Testhefte zusammengefasst
            // Item nur hinzufügen, wenn es noch nicht in der Liste ist (testheftabhängig)
            // if (
            //     itemErgebisseInSchule
            //         .map(i => `${i.testheft_id}-${i.item_id}`)
            //         .includes(`${e.testheft_id}-${e.item_id}`) === false
            // )
            if (itemErgebisseInSchule.map(i => i.item_id).includes(e.item_id) === false) {
                itemErgebisseInSchule.push({
                    testheft_idArr: [e.testheft_id],
                    item_id: e.item_id,
                    ergebnisse: [],
                    loesungshaeufigkeiten: {
                        anzahl: null,
                        r: null,
                        n: null,
                        f: null,
                        rProz: null,
                        nProz: null,
                        fProz: null
                    }
                });
            } else {
                if (
                    itemErgebisseInSchule
                        .filter(i => i.item_id === e.item_id)[0].testheft_idArr
                        .includes(e.testheft_id) === false
                )
                    itemErgebisseInSchule.filter(i => i.item_id === e.item_id)[0].testheft_idArr.push(e.testheft_id);
            }

            //region Testheft-Property nur auf das Testheft, das Modul, die Aufgabe, reduzieren, in denen dieses Item getestet wurde
            let ergebnisMitTestheftInSchule: IErgebnisseDB & { testheft: ITestheftAPI } = Utilities.cloneObject(e) as IErgebnisseDB & { testheft: ITestheftAPI };
            ergebnisMitTestheftInSchule.testheft = Utilities.cloneObject(this.testheftliste.filter(th => th.testheft_id === e.testheft_id)[0]);
            ergebnisMitTestheftInSchule.testheft.module = ergebnisMitTestheftInSchule.testheft.module
                .filter(m => m.aufgaben
                    .flatMap(a => a.items)
                    .flatMap(i => i.item_id).includes(e.item_id)
                );
            ergebnisMitTestheftInSchule.testheft.module[0].aufgaben = ergebnisMitTestheftInSchule.testheft.module[0].aufgaben
                .filter(a => a.items
                    .flatMap(i => i.item_id).includes(e.item_id)
                );
            ergebnisMitTestheftInSchule.testheft.module[0].aufgaben[0].items = ergebnisMitTestheftInSchule.testheft.module[0].aufgaben[0].items
                .filter(i => i.item_id === e.item_id
                );
            itemErgebisseInSchule.filter(i => i.item_id === e.item_id)[0].ergebnisse.push(ergebnisMitTestheftInSchule);
            //endregion

        });

        // Lösungshäufigkeiten für dieses Item berechnen
        itemErgebisseInSchule.forEach(i => {
            i.loesungshaeufigkeiten.anzahl = i.ergebnisse.length;
            i.loesungshaeufigkeiten.r = i.ergebnisse.filter(e => e.ergebnis === 1).length;
            i.loesungshaeufigkeiten.n = i.ergebnisse.filter(e => e.ergebnis === -1).length;
            i.loesungshaeufigkeiten.f = i.ergebnisse.filter(e => e.ergebnis === 0).length;

            let proz: number[] = [
                (i.loesungshaeufigkeiten.r / i.loesungshaeufigkeiten.anzahl) * 100,
                (i.loesungshaeufigkeiten.n / i.loesungshaeufigkeiten.anzahl) * 100,
                (i.loesungshaeufigkeiten.f / i.loesungshaeufigkeiten.anzahl) * 100
            ];
            let prozGerundet: number[] = Utilities.summenerhaltendRunden(proz);

            i.loesungshaeufigkeiten.rProz = prozGerundet[0];
            i.loesungshaeufigkeiten.nProz = prozGerundet[1];
            i.loesungshaeufigkeiten.fProz = prozGerundet[2];
        });
        //endregion

        // Item-Metadaten zentral ermitteln, damit Filter und Rendering dieselbe Quelle nutzen
        const getItemEigenschaften = (item: typeof itemErgebisse[number]) => {
            return this.testheftliste
                .filter(th => th.testheft_id === item.testheft_idArr[0])[0].module
                .flatMap(m => m.aufgaben
                    .flatMap(a => a.items)
                )
                .filter(i => i.item_id === item.item_id)[0] ?? null;
        };

        const getAufgabeEigenschaften = (item: typeof itemErgebisse[number]) => {
            return this.testheftliste
                .filter(th => th.testheft_id === item.testheft_idArr[0])[0].module
                .flatMap(m => m.aufgaben)
                .filter(a => a.items.some(i => i.item_id === item.item_id))[0] ?? null;
        };

        // Angezeigte Niveaustufe bestimmen, identisch zur späteren Ausgabe in der Itemzeile
        const getItemNiveaustufe = (item: typeof itemErgebisse[number]): string => {
            const itemEigenschaften = getItemEigenschaften(item);
            if (itemEigenschaften === null)
                return '';

            return Utilities.printNiveaustufe(itemEigenschaften.niveaustufe, item.ergebnisse[0].testheft.fach, 'NiveaustufeBez', itemEigenschaften.iqb_item_id, this.sessionvariablen.schule.land);
        };

        // Matchingtabelle für Spezialkompetenzstufen NRW 2026 hardgecoded
        const getItemNiveaustufeSortierung = (item: typeof itemErgebisse[number]): number => {
            const itemEigenschaften = getItemEigenschaften(item);
            if (itemEigenschaften === null)
                return Number.MAX_SAFE_INTEGER;

            if (this.sessionvariablen.schule.land === 'nw') {
                const niveaustufe = getItemNiveaustufe(item).replace(/^KS\s*/, '');
                const sortierungNw: Record<string, number> = {
                    'k.h.N.': 0,
                    '1': 1,
                    '1a': 2,
                    '1b': 3,
                    '2': 4,
                    '3': 5,
                    '4': 6,
                    '5': 7,
                };

                return sortierungNw[niveaustufe] ?? Number.MAX_SAFE_INTEGER;
            }

            return itemEigenschaften.niveaustufe ?? 1;
        };

        const normalisiereVergleichsgruppenWert = (wert: string | number | null | undefined): string => {
            return (wert ?? '').toString().trim().toLowerCase();
        };

        const getStandorttyp = (): string | null => {
            if (this.sessionvariablen.schule.land === 'nw')
                return this.sessionvariablen.schule.standorttyp_nw ?? null;

            if (this.sessionvariablen.schule.land === 'hb')
                return this.sessionvariablen.schule.standorttyp_hb ?? null;

            return null;
        };

        const getNwSubgruppe = (fach: string, testheftId: number): { subgruppe: string; subgruppeKuerzel: string } | null => {
            if (this.sessionvariablen.schule.land !== 'nw')
                return null;

            const vergleichsgruppeNw = this.vergleichsgruppenNwListe
                .filter(vg => {
                    return (
                        normalisiereVergleichsgruppenWert(vg.fach) === normalisiereVergleichsgruppenWert(fach) &&
                        normalisiereVergleichsgruppenWert(vg.schulform) === normalisiereVergleichsgruppenWert(this.sessionvariablen.schule.schulform) &&
                        normalisiereVergleichsgruppenWert(vg.kurs_niveau) === normalisiereVergleichsgruppenWert(this.gruppeAngezeigt?.kurs_niveau) &&
                        vg.testheft_id === testheftId
                    );
                })[0] ?? null;

            if (vergleichsgruppeNw === null || vergleichsgruppeNw.subgruppe === undefined)
                return null;

            return {
                subgruppe: vergleichsgruppeNw.subgruppe,
                subgruppeKuerzel: vergleichsgruppeNw.subgruppeKuerzel ?? vergleichsgruppeNw.subgruppe
            };
        };

        const getVergleichsgruppenwert = (item: typeof itemErgebisse[number]): { prozent: number; labelLang: string; labelKurz: string; tooltip: string } | null => {
            if (this.isVergleichsgruppeEnabled === false)
                return null;

            const land = this.sessionvariablen.schule.land;
            const standorttyp = getStandorttyp();
            if (standorttyp === null)
                return null;

            const testheftId = item.testheft_idArr[0];
            const fach = item.ergebnisse[0].testheft.fach;
            const nwSubgruppe = getNwSubgruppe(fach, testheftId);
            if (land === 'nw' && nwSubgruppe === null)
                return null;

            const vergleichsgruppenwert: ILhItemebeneVergleichsgruppen | null = this.lhItemebeneVergleichsgruppenliste
                .filter(lh => {
                    return (
                        lh.ebene === 'kontext' &&
                        lh.land === land &&
                        lh.testheft_id === testheftId &&
                        lh.item_id === item.item_id &&
                        normalisiereVergleichsgruppenWert(lh.kontextgruppe) === normalisiereVergleichsgruppenWert(standorttyp) &&
                        (land !== 'nw' || normalisiereVergleichsgruppenWert(lh.subgruppe) === normalisiereVergleichsgruppenWert(nwSubgruppe?.subgruppe))
                    );
                })[0] ?? null;

            if (vergleichsgruppenwert === null)
                return null;

            const prozent = Math.round(Number(vergleichsgruppenwert.proz_schueler) * 100);
            if (isNaN(prozent))
                return null;

            const subgruppeKuerzel = (land === 'nw') ? (nwSubgruppe?.subgruppeKuerzel ?? vergleichsgruppenwert.subgruppe_kuerzel ?? null) : null;
            const kontextgruppeBezeichnung = vergleichsgruppenwert.kontextgruppe_bezeichnung ?? null;
            const standortLabel = kontextgruppeBezeichnung ?? `Standort ${standorttyp}`;
            const labelDetail = subgruppeKuerzel ?? standortLabel;
            return {
                prozent,
                labelLang: (land === 'nw') ? 'Land' : `Vergleichsgruppe: ${labelDetail}`,
                labelKurz: (land === 'nw') ? 'Land' : labelDetail,
                tooltip: (land === 'nw') ? `Der Landeswert ergibt sich aus allen Schülerinnen und Schülern, die dasselbe Testheft in derselben Schulform bearbeitet haben. Dieses Item wurde dort in ${prozent}% der Fälle richtig gelöst`
                    : `In der Vergleichsgruppe ${labelDetail} wurde dieses Item in ${prozent}% der Fälle richtig gelöst.`
            };
        };

        const getAnzahlTooltip = (titel: string, anzahl: number, gesamt: number, prozent: number): string => Tooltip.html`
            <p><strong>${titel}</strong></p>
            <p>${anzahl} von ${gesamt} Schülern · <strong>${prozent} %</strong></p>
        `;

        const renderVergleichsbalken = (klasse: string, label: string, prozent: number, tooltip: string): string => `
            <div class="lh-balkenzeile ${klasse}">
                <span class="lh-balken-label">${label}</span>
                <div class="lh-balkenspur ${klasse}" data-tooltip="${tooltip}">
                    <span class="lh-balken-fuellung" data-prozent="${prozent}" style="width: ${prozent}%"><span class="lh-balken-prozentwert">${prozent}<span class="prozentzeichen">%</span></span></span>
                </div>
            </div>
        `;

        const renderGruppenbalken = (werte: ILoesungshaeufigkeitenWerte): string => {
            const richtigTooltip = getAnzahlTooltip('Gruppe: richtig beantwortet', werte.r, werte.anzahl, werte.rProz);

            if (this.isRnfAnsichtEnabled === false)
                return renderVergleichsbalken('gruppe', 'Gruppe', werte.rProz, richtigTooltip);

            const nichtBearbeitetTooltip = getAnzahlTooltip('Gruppe: nicht bearbeitet', werte.n, werte.anzahl, werte.nProz);
            const falschTooltip = getAnzahlTooltip('Gruppe: falsch beantwortet', werte.f, werte.anzahl, werte.fProz);

            return `
                <div class="lh-balkenzeile gruppe detail">
                    <span class="lh-balken-label">Gruppe</span>
                    <div class="lh-balkenspur gruppe">
                        <span class="lh-balken-segment richtig" data-prozent="${werte.rProz}" style="width: ${werte.rProz}%" data-tooltip="${richtigTooltip}"><span class="lh-balken-prozentwert">${werte.rProz}<span class="prozentzeichen">%</span></span></span>
                        <span class="lh-balken-segment nicht-bearbeitet" data-prozent="${werte.nProz}" style="width: ${werte.nProz}%" data-tooltip="${nichtBearbeitetTooltip}"><span class="lh-balken-prozentwert">${werte.nProz}<span class="prozentzeichen">%</span></span></span>
                        <span class="lh-balken-segment falsch" data-prozent="${werte.fProz}" style="width: ${werte.fProz}%" data-tooltip="${falschTooltip}"><span class="lh-balken-prozentwert">${werte.fProz}<span class="prozentzeichen">%</span></span></span>
                    </div>
                </div>
            `;
        };

        // Filteroptionen aus allen aktuell berechneten Items bilden, bevor der Filter angewendet wird
        const niveaustufenInAnzeige = new Map<string, number>();
        itemErgebisse.forEach(item => {
            const itemEigenschaften = getItemEigenschaften(item);
            if (itemEigenschaften === null)
                return;

            const niveaustufe = getItemNiveaustufe(item);
            if (niveaustufe === '')
                return;

            const sortierung = getItemNiveaustufeSortierung(item);
            const vorhandeneSortierung = niveaustufenInAnzeige.get(niveaustufe);
            if (vorhandeneSortierung === undefined || sortierung < vorhandeneSortierung) {
                niveaustufenInAnzeige.set(niveaustufe, sortierung);
            }
        });

        // Nicht mehr verfügbare Auswahl nach Fach- oder Gruppenwechsel zurücksetzen
        if (this.niveaustufeFilter !== '-' && niveaustufenInAnzeige.has(this.niveaustufeFilter) === false) {
            this.niveaustufeFilter = '-';
        }

        this.refreshNiveaustufeFilter(
            [...niveaustufenInAnzeige.entries()]
                .map(([wert, sortierung]) => ({wert, sortierung}))
                .sort((a, b) => a.sortierung - b.sortierung)
        );

        if (this.niveaustufeFilter !== '-') {
            itemErgebisse = itemErgebisse.filter(item => getItemNiveaustufe(item) === this.niveaustufeFilter);
        }

        const kompetenzEigenschaften = [
            'is_ma_kompetenz_K1',
            'is_ma_kompetenz_K2',
            'is_ma_kompetenz_K3',
            'is_ma_kompetenz_K4',
            'is_ma_kompetenz_K5',
            'is_ma_kompetenz_K6'
        ] as const;
        const hatMathematischeKompetenzwerte = this.sessionvariablen.get['fach'] === 'ma' && itemErgebisse.some(item => {
            const itemEigenschaften = getItemEigenschaften(item);
            return itemEigenschaften !== null && kompetenzEigenschaften.some(eigenschaft => itemEigenschaften[eigenschaft] === 1);
        });
        const aufgabenebeneContainer = document.querySelector<HTMLDivElement>(`div.rueckmeldung-lh-aufgabenebene`);
        aufgabenebeneContainer?.classList.toggle('mit-kompetenzbereichen', hatMathematischeKompetenzwerte);
        const kompetenzbereicheSpaltenkopf = aufgabenebeneContainer?.querySelector<HTMLDivElement>(`div.header > div.kompetenzbereiche`) ?? null;
        if (kompetenzbereicheSpaltenkopf !== null)
            kompetenzbereicheSpaltenkopf.hidden = !hatMathematischeKompetenzwerte;

        //region Einträge nach ausgewählter Spalte sortieren
        let kategorie: string = document.querySelector(`[data-sort-kategorie][data-sort-richtung]`)?.getAttribute('data-sort-kategorie') ?? null;
        let richtung: string = document.querySelector(`[data-sort-kategorie][data-sort-richtung]`)?.getAttribute('data-sort-richtung') ?? null;

        if (kategorie === null || kategorie === 'eigenschaften') {
            itemErgebisse.sort((a, b) => {
                let posA = a.ergebnisse[0].testheft.module[0].position_nr * 10000 +
                    a.ergebnisse[0].testheft.module[0].aufgaben[0].position_nr * 100 +
                    a.ergebnisse[0].testheft.module[0].aufgaben[0].items[0].position_nr;
                let posB = b.ergebnisse[0].testheft.module[0].position_nr * 10000 +
                    b.ergebnisse[0].testheft.module[0].aufgaben[0].position_nr * 100 +
                    b.ergebnisse[0].testheft.module[0].aufgaben[0].items[0].position_nr;

                return (
                    posA < posB
                ) ? -1 : 1;
            });
        }
        if (kategorie === 'loesungshaeufigkeit') {
            itemErgebisse.sort((a, b) => {
                if (a.loesungshaeufigkeiten === null && b.loesungshaeufigkeiten === null) return 0;
                if (a.loesungshaeufigkeiten === null) return -1;
                if (b.loesungshaeufigkeiten === null) return 1;

                if (
                    (a.loesungshaeufigkeiten.rProz * 100000 + a.loesungshaeufigkeiten.nProz * 1000 + a.loesungshaeufigkeiten.fProz) >
                    (b.loesungshaeufigkeiten.rProz * 100000 + b.loesungshaeufigkeiten.nProz * 1000 + b.loesungshaeufigkeiten.fProz)
                )
                    return 1;

                if (
                    (a.loesungshaeufigkeiten.rProz * 100000 + a.loesungshaeufigkeiten.nProz * 1000 + a.loesungshaeufigkeiten.fProz) <
                    (b.loesungshaeufigkeiten.rProz * 100000 + b.loesungshaeufigkeiten.nProz * 1000 + b.loesungshaeufigkeiten.fProz)
                )
                    return -1;

                return 0;
            });
        }
        if (kategorie === 'domaene') {
            itemErgebisse.sort((a, b) => {
                let domA = this.domaeneEigenschaften(a.ergebnisse[0].testheft.module[0].aufgaben[0].items[0].domaene).kurzbezeichnung;
                let domB = this.domaeneEigenschaften(b.ergebnisse[0].testheft.module[0].aufgaben[0].items[0].domaene).kurzbezeichnung;

                return (
                    domA < domB
                ) ? -1 : 1;
            });
        }

        // Sortierung umkehren
        if (richtung === 'desc') {
            itemErgebisse.reverse();
        }
        //endregion

        // region Tabelle in DOM rendern
        //TODO:medium der Landeswert ist hier immer der erste vorkommende - wenn mehrere Testhefte in Stammklasse geschrieben werden, müssten diese aufgeschlüsselt werden
        document.querySelector(`div.rueckmeldung-lh-aufgabenebene > div:nth-of-type(2)`)
            .innerHTML = itemErgebisse.map(item => {
            let itemEigenschaften = getItemEigenschaften(item);

            if (itemEigenschaften === null)
                return ``;

            const itemNiveaustufe = getItemNiveaustufe(item);
            const aufgabeEigenschaften = getAufgabeEigenschaften(item);

            if (aufgabeEigenschaften === null)
                return ``;

            let schulwert: ILoesungshaeufigkeitenWerte;
            try {
                schulwert = itemErgebisseInSchule
                    .filter(i => i.item_id === item.item_id && i.testheft_idArr.includes(item.testheft_idArr[0]))[0].loesungshaeufigkeiten ?? null;
            } catch {
                schulwert = null;
            }

            let landeswert: number | null;
            try {
                landeswert = Math.round(this.lhItemebeneVergleichsgruppenliste
                    .filter(lh => {
                        return (
                            lh.ebene === 'land' &&
                            lh.land === this.sessionvariablen.schule.land &&
                            lh.testheft_id === item.testheft_idArr[0] &&
                            lh.item_id === item.item_id
                        )
                    })[0]?.proz_schueler * 100) ?? null;
            } catch {
                landeswert = null;
            }

            const vergleichsgruppenwert = getVergleichsgruppenwert(item);
            const aufgabe = item.ergebnisse[0].testheft.module[0].aufgaben[0];
            const testheft = this.testheftliste.find(eintrag => eintrag.testheft_id === item.testheft_idArr[0]) ?? null;
            const aufgabePosition = (this.sessionvariablen.projekt.weiche === 'vera5')
                ? Utilities.getVera5Aufgabenposition(testheft, aufgabe.aufgabe_id, aufgabe.position_nr)
                : aufgabe.position_nr;
            const itemBezeichnung = (this.sessionvariablen.projekt.weiche === 'vera5')
                ? Utilities.printVera5Aufgabenposition(aufgabePosition, aufgabe.items[0].position_bezeichnung, aufgabeEigenschaften.items.length)
                : `${(this.sessionvariablen.get['fach'] !== 'ma') ? this.domaeneEigenschaften(item.ergebnisse[0].testheft.module[0].domaene).kurzbezeichnung : ``} ${item.ergebnisse[0].testheft.module[0].typ.toUpperCase()} ${Utilities.printSchwierigkeit(item.ergebnisse[0].testheft.fach, item.ergebnisse[0].testheft.module[0].schwierigkeit)} ${aufgabe.position_nr}.${aufgabe.items[0].position_bezeichnung}`;
            const aufgabeTooltipAttribute = (item.ergebnisse[0].testheft.fach === 'ma') ? '' : `data-tooltip-aufgabe-id="${aufgabe.aufgabe_id}" data-tooltip-testheft-id="${item.testheft_idArr[0]}"`;
            const isqAufgabenbrowserLink = (this.sessionvariablen.projekt.weiche !== 'vera5' && this.sessionvariablen.schule.land === 'nw' && itemEigenschaften.isq_aufgabenbrowser_deeplink !== null) ? `
                <a class="isq-aufgabenbrowser-link no-print" href="https://www.aufgabenbrowser.de/itemdb/view/IQB_V${itemEigenschaften.projekt.slice(-1)}_${itemEigenschaften.jahrgang}_${itemEigenschaften.isq_aufgabenbrowser_deeplink}" target="isq-aufgabenbrowser" aria-label="Item im ISQ Aufgabenbrowser öffnen" data-tooltip="${Tooltip.html`
                    <p><strong>ISQ Aufgabenbrowser</strong></p>
                    <p>Öffnet dieses Item im externen Aufgabenbrowser.</p>
                `}">
                    <svg role="graphics-symbol">
                        <use href="/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#journal-bookmark"/>
                    </svg>
                </a>
            ` : ``;
            const domaenenEigenschaften = this.domaeneEigenschaften(item.ergebnisse[0].testheft.module[0].aufgaben[0].items[0].domaene);
            const domaenenKurzbeschreibungUrl = TooltipItem.getDomaenenKurzbeschreibungUrl(
                this.sessionvariablen.projekt.weiche,
                item.ergebnisse[0].testheft.fach,
                domaenenEigenschaften.domaene ?? ''
            );
            const domaenenBezeichnung = (this.sessionvariablen.get['fach'] !== 'ma') ? 'Domäne' : 'Leitidee';
            const domaenenWert = (domaenenKurzbeschreibungUrl === null)
                ? `<span>${domaenenEigenschaften.kurzbezeichnung}</span>`
                : `<a class="domaenen-material-link" href="${domaenenKurzbeschreibungUrl}" target="_blank" rel="noopener" aria-label="${domaenenBezeichnung} ${domaenenEigenschaften.bezeichnung}: Kurzbeschreibung als PDF öffnen">${domaenenEigenschaften.kurzbezeichnung}</a>`;

            return `
            <div class="item" data-itemid="${item.item_id.toString()}">
                <div class="eigenschaften">
                    <div class="itembezeichnung">
                        <span class="itemname" data-tooltip-item-id="${item.item_id.toString()}" data-tooltip-testheft-id="${item.testheft_idArr[0]}">${itemBezeichnung}</span>
                        ${isqAufgabenbrowserLink}
                    </div>
                    <div class="aufgabenname"><span ${aufgabeTooltipAttribute}>${aufgabe.bezeichnung}</span></div>
                    ${(itemNiveaustufe !== '') ? `<div class="itemniveau">${itemNiveaustufe}</div>` : ``}
                </div>
                <div class="loesungshaeufigkeit">
            ${(item.ergebnisse.length > 0) ? `
                    ${renderGruppenbalken(item.loesungshaeufigkeiten)}` : `<span class="keine-ergebnisse">Bisher wurden noch keine Ergebnisse für dieses Item eingetragen.</span>`
            }
            ${(this.isSchulwertEnabled === true && schulwert !== null) ? `
                    ${renderVergleichsbalken('schulwert', 'Schule', schulwert.rProz, getAnzahlTooltip('Schule: richtig beantwortet', schulwert.r, schulwert.anzahl, schulwert.rProz))}` : ``
            }
            ${(this.isLandeswertEnabled === true && landeswert !== null && !isNaN(landeswert)) ? `
                    ${renderVergleichsbalken('landeswert', 'Land', landeswert, Tooltip.html`
                        <p><strong>Land: richtig beantwortet</strong></p>
                        <p>In Ihrem Bundesland wurde dieses Item in <strong>${landeswert}%</strong> der Fälle richtig gelöst.</p>
                    `)}` : ``
            }
            ${(vergleichsgruppenwert !== null) ? `
                    ${renderVergleichsbalken('vergleichsgruppe', vergleichsgruppenwert.labelKurz, vergleichsgruppenwert.prozent, Tooltip.html`
                        <p><strong>${vergleichsgruppenwert.labelLang}: richtig beantwortet</strong></p>
                        <p>${vergleichsgruppenwert.tooltip}</p>
                    `)}` : ``
            }
                </div>
                <div class="domaene" data-tooltip="${Tooltip.html`
                    <p><strong>${domaenenBezeichnung}</strong></p>
                    <p>${domaenenEigenschaften.bezeichnung}</p>
                `}">
                    ${domaenenWert}
                </div>
               
                ${(hatMathematischeKompetenzwerte === true) ? `
                <div class="kompetenzbereiche">
                    ${(itemEigenschaften.is_ma_kompetenz_K1 === 1) ? `<span class="mathematische-kompetenz" data-tooltip="${Tooltip.html`
                        <p><strong>K1 – Mathematisch argumentieren</strong></p>
                        <p>Hierbei geht es um das Verstehen, Verbinden und Bewerten mathematisch-logischer Argumentationsketten.</p>
                    `}">K1</span>` : ``}
                    ${(itemEigenschaften.is_ma_kompetenz_K2 === 1) ? `<span class="mathematische-kompetenz" data-tooltip="${Tooltip.html`
                        <p><strong>K2 – Probleme mathematisch lösen</strong></p>
                        <p>Wenn eine Lösungsstruktur nicht offensichtlich ist, bedarf es eines strategischen Vorgehens zur Auffindung mathematischer Lösungsideen wie beispielsweise Analogie, systematisches Probieren, Veranschaulichung, welche in Hinblick auf ihre Brauchbarkeit hin im Lösungsprozess angewendet, überprüft und bewertet werden müssen.</p>
                    `}">K2</span>` : ``}
                    ${(itemEigenschaften.is_ma_kompetenz_K3 === 1) ? `<span class="mathematische-kompetenz" data-tooltip="${Tooltip.html`
                        <p><strong>K3 – Mathematisch modellieren</strong></p>
                        <p>Realitätsbezogene Fragestellungen gilt es, in mathematische Modelle als reduzierte, vereinfachte Abbilder zu überführen, das Problem mit mathematischen Mitteln zu lösen und dieses Resultat vor dem realen Kontext zu bewerten.</p>
                    `}">K3</span>` : ``}
                    ${(itemEigenschaften.is_ma_kompetenz_K4 === 1) ? `<span class="mathematische-kompetenz" data-tooltip="${Tooltip.html`
                        <p><strong>K4 – Mathematische Darstellungen verwenden</strong></p>
                        <p>Diese Kompetenz umfasst sowohl das Entwickeln geeigneter mathematische Darstellungen (Diagramme, Graphen, Formeln etc.) als auch das reflektierte Umgehen mit vorgegebenen mathematischen Repräsentationen.</p>
                    `}">K4</span>` : ``}
                    ${(itemEigenschaften.is_ma_kompetenz_K5 === 1) ? `<span class="mathematische-kompetenz" data-tooltip="${Tooltip.html`
                        <p><strong>K5 – Mit symbolischen, formalen und technischen Elementen der Mathematik umgehen</strong></p>
                        <p>Diese Kompetenz bezieht sich auf den Gebrauch mathematischer Fakten („Wissen, dass“) oder mathematischer Fertigkeiten („Wissen, wie“) und beinhaltet unter anderem das Kennen und Anwenden mathematischer Definitionen und Regeln, das formale Arbeiten mit Variablen, Termen und Funktionen sowie die Verwendung von Hilfsmitteln wie Formelsammlung und Taschenrechner.</p>
                    `}">K5</span>` : ``}
                    ${(itemEigenschaften.is_ma_kompetenz_K6 === 1) ? `<span class="mathematische-kompetenz" data-tooltip="${Tooltip.html`
                        <p><strong>K6 – Mathematisch kommunizieren</strong></p>
                        <p>Dies bezieht sich zum einen auf das Verstehen von Texten oder mündlichen Äußerungen zur Mathematik und zum anderen auf das verständliche schriftliche oder mündliche Darstellen und Präsentieren von Überlegungen, Lösungswegen und Ergebnissen.</p>
                        <p>In Abgrenzung zur Kompetenz K1 wird hier Wert auf das verständliche Darstellen, Präsentieren und Erläutern eines Lösungsweges gegenüber einem, auch fiktiven, sogenannten externen Adressaten gelegt.</p>
                    `}">K6</span>` : ``}
                </div>
                ` : ``}
            </div>`;
        }).join('');
        // endregion

        this.refreshBalkenbeschriftungen();

        if (this.tresor?.offen === true)
            this.tresor.fillTresorLuecken();
    }

    /**
     * Ergänzt den Niveaustufen-Filter in der vorhandenen Fach-/Gruppe-Filterzeile.
     * Die Erzeugung liegt hier, damit der Filter nur auf der Aufgabenebene erscheint
     */
    private renderNiveaustufeFilter(): void {
        const filterZeile = document.querySelector<HTMLDivElement>(`div#fach-gruppe-filter > div:first-of-type`);
        if (filterZeile === null || filterZeile.querySelector(`#niveaustufe-filter`) !== null)
            return;

        filterZeile.insertAdjacentHTML('beforeend', `
            <div class="niveaustufe">
                <label for="niveaustufe-filter">Niveaustufe:</label>
                <select id="niveaustufe-filter" class="niveaustufe-filter-select">
                    <option value="-" selected>Alle Niveaustufen</option>
                </select>
            </div>
        `);
    }

    /** Verschiebt die vorhandene Detailansicht-Option in den Fach-/Gruppe-Filter */
    private positioniereDetailansichtOption(): void {
        const filter = document.querySelector<HTMLDivElement>(`div#fach-gruppe-filter`);
        const filterZeile = filter?.querySelector<HTMLDivElement>(`div.filterauswahl`) ?? null;
        const detailansichtOption = document.querySelector<HTMLDivElement>(`div.rueckmeldung-anzeigeoptionen`);
        if (filter === null || filterZeile === null || detailansichtOption === null)
            return;

        const gruppeFilter = filterZeile.querySelector<HTMLDivElement>(`div.gruppe`);
        if (filterZeile.style.display !== 'none' && gruppeFilter !== null)
            gruppeFilter.insertAdjacentElement('afterend', detailansichtOption);
        else
            filter.insertBefore(detailansichtOption, filter.querySelector(`section.gruppeninformation`));
    }

    /**
     * Aktualisiert die auswählbaren Niveaustufen anhand der aktuell vorhandenen Items.
     * Die TomSelect-Instanz wird neu aufgebaut, weil sich die Optionsliste nach Fach- oder Gruppenwechsel ändern kann
     */
    private refreshNiveaustufeFilter(optionen: { wert: string; sortierung: number }[]): void {
        const select = document.querySelector<HTMLSelectElement>(`select#niveaustufe-filter`);
        if (select === null)
            return;

        const filterContainer = select.closest<HTMLDivElement>(`div.niveaustufe`);
        if (filterContainer !== null)
            filterContainer.hidden = optionen.length === 0;

        const druckNiveaustufeContainer = document.querySelector<HTMLDivElement>(`#rueckmeldung-druckniveaustufe-container`);
        const druckNiveaustufe = document.querySelector<HTMLElement>(`#rueckmeldung-druckniveaustufe`);
        if (druckNiveaustufeContainer !== null)
            druckNiveaustufeContainer.hidden = optionen.length === 0;
        if (druckNiveaustufe !== null)
            druckNiveaustufe.textContent = (this.niveaustufeFilter === '-') ? 'Alle Niveaustufen' : this.niveaustufeFilter;

        try {
            // @ts-ignore
            select.tomselect.destroy();
        } catch {
            //
        }

        select.innerHTML = `
            <option value="-">Alle Niveaustufen</option>
            ${optionen.map(option => `<option value="${option.wert}" ${(option.wert === this.niveaustufeFilter) ? `selected="selected"` : ``}>${option.wert}</option>`).join('')}
        `;

        try {
            // @ts-ignore
            new TomSelect(`#niveaustufe-filter`, {
                create: false,
                allowEmptyOption: true,
                controlInput: null,
                searchField: [],
                onChange: (value: string): void => {
                    this.niveaustufeFilter = value || '-';
                    document.dispatchEvent(new Event('refresh-rueckmeldung'));
                }
            });

            // @ts-ignore
            select.tomselect.setValue(this.niveaustufeFilter, true);
            if (this.rmFilter.gruppeId === null) {
                // @ts-ignore
                select.tomselect.disable();
            } else {
                // @ts-ignore
                select.tomselect.enable();
            }
        } catch (e) {
            Utilities.errorToSentry(e);
        }
    }

    /** Aktualisiert die fachlichen Angaben und die Legende des Ausdrucks */
    private refreshDruckkopf(): void {
        const druckFach = document.querySelector<HTMLElement>(`#rueckmeldung-druckfach`);
        const druckGruppe = document.querySelector<HTMLElement>(`#rueckmeldung-druckgruppe`);
        const druckLegende = document.querySelector<HTMLElement>(`#rueckmeldung-drucklegende`);
        const fach = this.rmFilter?.fach ?? this.sessionvariablen.get['fach'] ?? null;

        if (druckFach !== null)
            druckFach.textContent = (fach === null || fach === 'alle') ? '' : Utilities.printFachLang(fach);
        if (druckGruppe !== null)
            druckGruppe.textContent = this.gruppeAngezeigt?.bezeichnung ?? '';
        if (druckLegende !== null)
            druckLegende.textContent = (this.isRnfAnsichtEnabled === true)
                ? 'Gruppenbalken von links: richtig · nicht bearbeitet · falsch. Vergleichsbalken: richtig gelöst.'
                : 'Alle Balken zeigen den Anteil richtig gelöster Items.';
    }
}
